import dayjs from "dayjs";
import {
  Box,
  Calendar,
  Check,
  CheckCircle,
  Clock,
  Copy,
  Edit,
  Plus,
  Truck,
  User,
  FileSignature,
  X,
  List,
  Download,
  FileSpreadsheet,
  FileText,
  CalendarDays,
  Printer,
  Link2,
} from "lucide-react";
import { FC, useState, useCallback, useEffect, useRef } from "react";
import { Form, Popconfirm, Spin, Table, Segmented, Tag, Modal, message } from "antd";
import { ServiceStats } from "./service-stats";
import { ServicesCalendarView } from "./services-calendar-view";
import { useQueryClient } from "@tanstack/react-query";
import {
  capitalize,
  calculateCubicFeetInventory,
  formatDimensions,
  formatFullAddress,
  formatPackingType,
  getClientName,
  parseNumber,
  prepareServiceTicketDatesForAPI,
} from "~/utils";
import { JobStatus, ServiceType, Urgency } from "~/utils/constants";
import { getStatusBadge } from "../booking-utils";
import { useDetailBookingStore } from "../detail-booking.store";
import { PrintWaybill } from "~/components/admin/dispatch/print-labels/print-waybill";
import { PrintLabels } from "~/components/admin/dispatch/print-labels/print-label";
import {
  CompleteServiceTicketModal,
  useCompleteTicket,
} from "~/components/common/complete-ticket";
import { TInventory, TServiceTicket, TServiceTicketSupplyItem } from "~/types";
import { BOOKING_KEYS } from "~/services/booking";
import {
  useCreateServiceTicket,
  useDeleteServiceTicket,
  useUpdateServiceTicket,
} from "~/services/service-ticket";
import { useNotification } from "~/hooks";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { serviceIcons } from "~/utils/service-icons";
import { ServiceForm } from "../../booking-form/service-tickets-form";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { EstimateServiceForm } from "../estimate-service-form";
import { DuplicateServiceModal, DuplicateOptions } from "./duplicate-service-modal";
import { ExportModal, ExportType } from "~/components/client/inventory/export-modal";
import { exportToCSV, exportToPDF } from "~/components/client/inventory/export-utils";
import { ServiceTicketExpoForm } from "../expo-service-form";



type FormValues = {
  id?: string;
  startDatetime: Date | null;
  endDatetime: Date | null;
  serviceDatetime: Date | null;
  duration: number;
  addOnServices: string[];
  urgency: Urgency;
  serviceType: ServiceType;
  staffNotes: string;
  serial: string;
  timeIn: Date | null;
  timeOut: Date | null;
  completedAt: Date | null;
  status: JobStatus;
  clientId: string;
  jobTicketId: string;
  vesselId: string;
  shippingAddressId: string;
  deliveryLocationId: string;
  staffIds: string[];
  inventoryIds: string[];
  supplyItems: TServiceTicketSupplyItem[];
  dueDate: Date | null;
  scope: string;
};
import { ServiceCompletionDetails } from "~/components/common/service-completion-details";
import { useBooking } from "~/store";
import { useEmailConfirmation } from "~/hooks/useEmailConfirmation";
import { EmailRecipient } from "~/components/common/email-confirmation-modal";
import { useGetAllStaff } from "~/services/operation";

type Props = {};

export const Services: FC<Props> = () => {
  // Get serviceId from URL parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceIdFromUrl = searchParams.get('serviceId');

  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [viewType, setViewType] = useState<"list" | "calendar">("list");
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [ticketToDuplicate, setTicketToDuplicate] = useState<TServiceTicket | null>(null);
  const editingTicketIdRef = useRef<string | null>(null);
  const hasProcessedUrlParam = useRef(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>("csv");
  const [expoModalServiceId, setExpoModalServiceId] = useState<string | null>(null);
  const { setClientId } = useBooking();
  const [form] = Form.useForm<FormValues>();
  const { openCompleteTicket } = useCompleteTicket();
  const { booking, showServiceForm, setShowServiceForm, showEstimateForm, setShowEstimateForm, isAddingNewService, setIsAddingNewService, setEditingEstimateService } = useDetailBookingStore();

  // Check if expo mode is available (booking has a project)
  const isExpoModeAvailable = !!booking?.projectId;

  // Debug expo mode
  console.log('Expo Debug:', {
    bookingProjectId: booking?.projectId,
    isExpoModeAvailable,
    selectedServiceId,
    selectedServiceType: booking?.serviceTicket?.find(s => s.id === selectedServiceId)?.serviceType,
  });
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const { showConfirmation, ConfirmationModal } = useEmailConfirmation();
  const { data: allStaffData } = useGetAllStaff();

  // Auto-select service based on URL parameter (only once)
  useEffect(() => {
    if (serviceIdFromUrl && booking?.serviceTicket && !hasProcessedUrlParam.current) {
      // Check if the service exists in the booking
      const serviceExists = booking.serviceTicket.find(s => s.id === serviceIdFromUrl);
      if (serviceExists) {
        console.log('Auto-selecting service:', serviceIdFromUrl);
        setSelectedServiceId(serviceIdFromUrl);
        hasProcessedUrlParam.current = true;

        // Clear the serviceId from URL to prevent re-triggering
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('serviceId');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [serviceIdFromUrl, booking?.serviceTicket, searchParams, setSearchParams]);

  // Auto-open estimate form if quick estimate data exists
  useEffect(() => {
    if (booking?.id && !showEstimateForm && !showServiceForm) {
      const quickEstimateKey = `quickEstimate_${booking.id}`;
      const quickEstimateDataStr = sessionStorage.getItem(quickEstimateKey);
      
      if (quickEstimateDataStr) {
        // Auto-open the estimate form - it will load the data in its own useEffect
        setShowEstimateForm(true);
        setEditingEstimateService(null); // Ensure we're in "add" mode, not edit mode
      }
    }
  }, [booking?.id, showEstimateForm, showServiceForm, setShowEstimateForm, setEditingEstimateService]);

  const onUseCurrentTicket = useCallback(
    (ticket: TServiceTicket) => {
      form.setFieldsValue({
        ...ticket,
        serviceType: ticket.serviceType,
        shippingAddressId: ticket.location?.id,
        deliveryLocationId: ticket.deliveryLocation?.id,
        serviceDatetime: ticket.serviceDatetime
          ? dayjs(ticket.serviceDatetime)
          : null,
        startDatetime: ticket.startDatetime
          ? dayjs(ticket.startDatetime)
          : null,
        endDatetime: ticket.endDatetime ? dayjs(ticket.endDatetime) : null,
        clientId: ticket.clientId,
        vesselId: ticket.vessel?.id,
        inventoryIds: ticket.inventories?.map((inv) => inv.id),
        staffIds: ticket.staffs?.map((staff) => staff.id),
        addOnServices: ticket.addOnServices?.map((service) => service.id),
        urgency: ticket.urgency,
        staffNotes: ticket.staffNotes,
        duration: parseNumber(ticket.duration),
        supplyItems: ticket.supplyItems?.map((supply) => ({
          supplyId: supply.supplyId,
          quantity: supply.quantity,
          unitCost: Number(supply.unitCost ?? 0),
        })),
        id: ticket.id,
        dueDate: ticket.dueDate ? dayjs(ticket.dueDate) : null,
        scope: ticket.scope,
      });
      setShowServiceForm(true);
      setIsAddingNewService(false);
      setClientId(booking?.clientId ?? "");
      // Keep the ticket selected when editing
      setSelectedServiceId(ticket.id);
      // Track which ticket is being edited
      editingTicketIdRef.current = ticket.id;
    },
    [booking?.clientId, form, setClientId]
  );

  const mutation = useCreateServiceTicket();
  const mutationUpdate = useUpdateServiceTicket();
  const deleteServiceTicket = useDeleteServiceTicket();


  const clearForm = () => {
    form.resetFields();
    form.setFieldValue("serviceType", ServiceType.COLLECTION);
    setShowServiceForm(false);
    editingTicketIdRef.current = null;
  };

  const handleSubmit = async (status: JobStatus, createNew = false) => {
    const values = await form.validateFields();
    if (!booking) return;

    // Check if staff are being assigned (either new service with staff, or updating with new staff)
    const currentStaffIds = values.id
      ? booking.serviceTicket?.find(t => t.id === values.id)?.staffs?.map(s => s.id) || []
      : [];
    const newStaffIds = values.staffIds || [];
    const newlyAssignedStaffIds = newStaffIds.filter(id => !currentStaffIds.includes(id));

    // If there are newly assigned staff, show email confirmation modal
    if (newlyAssignedStaffIds.length > 0 && allStaffData?.items) {
      // Get staff email data from allStaffData
      const recipients: EmailRecipient[] = newlyAssignedStaffIds
        .map(staffId => {
          const staff = allStaffData.items.find(s => s.id === staffId);
          if (staff?.user?.email) {
            return {
              email: staff.user.email,
              name: `${staff.user.firstName || ''} ${staff.user.lastName || ''}`.trim() || 'Unknown',
              role: staff.user.role || 'STAFF'
            };
          }
          return null;
        })
        .filter((r): r is EmailRecipient => r !== null);

      // Only show modal if we have valid email recipients
      if (recipients.length > 0) {
        showConfirmation(recipients, {
          actionDescription: values.id
            ? `Assigning ${recipients.length} staff member(s) to service ticket ${values.serial || 'NEW'}`
            : `Assigning ${recipients.length} staff member(s) to new service ticket`,
          onProceed: async (sendEmails: boolean) => {
            await submitForm(status, createNew, !sendEmails);
          },
        });
        return; // Exit early - the modal will handle submission
      }
    }

    // No staff assignments or no email recipients - proceed directly
    await submitForm(status, createNew, false);
  };

  const submitForm = async (status: JobStatus, createNew: boolean, skipEmailNotifications: boolean) => {
    const values = form.getFieldsValue();
    if (!booking) return;

    // Transform dates to handle timezones properly (converts serviceDatetime to noon UTC,
    // combines startDatetime with serviceDatetime's date if they differ)
    const transformedValues = prepareServiceTicketDatesForAPI(values);

    const fields = {
      ...transformedValues,
      status,
      jobTicketId: booking?.id,
      skipEmailNotifications,
    };

    const refetchQueries = () => {
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKING(booking.id),
      });
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKINGS({}),
      });

      if (createNew) {
        clearForm();
      }
    };

    if (values.id) {
      mutationUpdate.mutate(fields, {
        onSuccess: () => {
          refetchQueries();
          success({
            message: "Service ticket updated successfully",
          });
          setShowServiceForm(false);
          // Re-select the ticket that was just edited
          if (editingTicketIdRef.current) {
            setSelectedServiceId(editingTicketIdRef.current);
            editingTicketIdRef.current = null;
          }
        },
        onError: (err) => {
          error({
            message: "Failed to update service ticket",
            description: err.message,
          });
        },
      });
    } else {
      mutation.mutate(fields, {
        onSuccess: () => {
          refetchQueries();
          success({
            message: "Service ticket created successfully",
          });
          setShowServiceForm(false);
        },
        onError: (err) => {
          error({
            message: "Failed to create service ticket",
            description: err.message,
          });
        },
      });
    }
  };

  const handleDeleteServiceTicket = async (ticketId: string) =>
    deleteServiceTicket.mutateAsync(ticketId, {
      onSuccess: () => {
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(booking?.id || ""),
        });
        success({
          message: "Service ticket deleted successfully",
        });
      },
      onError: (err) => {
        error({
          message: "Failed to delete service ticket",
          description: err.message,
        });
      },
    });

  const handleAddService = () => {
    clearForm();
    setShowServiceForm(true);
    setIsAddingNewService(true);
    setClientId(booking?.clientId ?? "");
  };

  const handleOpenDuplicateModal = (ticket: TServiceTicket) => {
    setTicketToDuplicate(ticket);
    setDuplicateModalOpen(true);
  };

  const handleEditEstimate = (service: TServiceTicket) => {
    setEditingEstimateService(service);
    setShowEstimateForm(true);
  };

  const handleConvertToService = (service: TServiceTicket) => {
    // Pre-populate the full service form with estimate data
    form.setFieldsValue({
      id: service.id, // Keep the same ID to update the existing ticket
      serviceType: service.serviceType,
      clientId: service.clientId || booking?.clientId,
      shippingAddressId: service.location?.id,
      serviceDatetime: service.serviceDatetime ? dayjs(service.serviceDatetime) : null,
      startDatetime: service.startDatetime ? dayjs(service.startDatetime) : null,
      duration: parseNumber(service.duration),
      urgency: service.urgency,
      vesselId: service.vessel?.id,
      staffNotes: '', // Don't copy staffNotes from estimate - only scope carries over
      scope: service.scope,
      addOnServices: service.addOnServices?.map((s) => s.id),
      // Don't copy inventoryIds - user needs to select actual inventory
      inventoryIds: [],
      staffIds: [],
      // Carry over supplies from estimate
      supplyItems: service.supplyItems?.map((supply) => ({
        supplyId: supply.supplyId,
        quantity: supply.quantity,
        unitCost: Number(supply.unitCost ?? 0),
      })) || [],
    });

    setShowServiceForm(true);
    setIsAddingNewService(false); // We're editing, not adding
    setClientId(booking?.clientId ?? "");
    setSelectedServiceId(service.id);
    editingTicketIdRef.current = service.id;
  };

  const handleDuplicateService = (options: DuplicateOptions) => {
    if (!ticketToDuplicate || !booking) return;

    const { includeVessel, includeStaff, includeInventory, serviceDate, startTime } = options;

    // Build the combined datetime for serviceDatetime
    let newServiceDatetime = null;
    let newStartDatetime = null;

    if (serviceDate) {
      newServiceDatetime = serviceDate;

      if (startTime) {
        // Combine date and time for startDatetime
        newStartDatetime = serviceDate
          .hour(startTime.hour())
          .minute(startTime.minute())
          .second(0);
      }
    }

    // Check if this is an estimate - use estimate form instead
    if (ticketToDuplicate.status === JobStatus.ESTIMATED) {
      // Create a duplicate object for the estimate form (without id so it creates new)
      const duplicateEstimate = {
        ...ticketToDuplicate,
        id: '', // Clear ID so it creates a new one
        serial: '', // Clear serial for new ticket
        // Apply new date/time if provided
        serviceDatetime: (newServiceDatetime?.toISOString() || ticketToDuplicate.serviceDatetime) as Date,
        startDatetime: (newStartDatetime?.toISOString() || ticketToDuplicate.startDatetime) as Date,
        // Apply conditional fields
        vessel: includeVessel ? ticketToDuplicate.vessel : undefined,
        staffs: includeStaff ? ticketToDuplicate.staffs : [],
        inventories: includeInventory ? ticketToDuplicate.inventories : [],
      } as TServiceTicket;

      setDuplicateModalOpen(false);
      setTicketToDuplicate(null);
      // Use the editing flow but the form will detect no ID and create new
      setEditingEstimateService(duplicateEstimate);
      setShowEstimateForm(true);
      return;
    }

    // Regular service ticket duplicate flow
    // Populate the form with the duplicated data
    form.setFieldsValue({
      // Copy all basic fields from original ticket
      serviceType: ticketToDuplicate.serviceType,
      clientId: ticketToDuplicate.clientId,
      shippingAddressId: ticketToDuplicate.location?.id,
      deliveryLocationId: ticketToDuplicate.deliveryLocation?.id,
      urgency: ticketToDuplicate.urgency,
      staffNotes: ticketToDuplicate.staffNotes,
      duration: parseNumber(ticketToDuplicate.duration),
      scope: ticketToDuplicate.scope,
      addOnServices: ticketToDuplicate.addOnServices?.map((service) => service.id),
      supplyItems: ticketToDuplicate.supplyItems?.map((supply) => ({
        supplyId: supply.supplyId,
        quantity: supply.quantity,
        unitCost: Number(supply.unitCost ?? 0),
      })),

      // New date/time
      serviceDatetime: newServiceDatetime,
      startDatetime: newStartDatetime,

      // Conditional fields based on user selection
      vesselId: includeVessel ? ticketToDuplicate.vessel?.id : undefined,
      staffIds: includeStaff ? ticketToDuplicate.staffs?.map((staff) => staff.id) : [],
      inventoryIds: includeInventory ? ticketToDuplicate.inventories?.map((inv) => inv.id) : [],

      // Don't copy these - it's a new ticket
      id: undefined,
      serial: undefined,
      timeIn: undefined,
      timeOut: undefined,
      completedAt: undefined,
      endDatetime: undefined,
      dueDate: ticketToDuplicate.dueDate ? dayjs(ticketToDuplicate.dueDate) : undefined,
    });

    // Close the modal and open the form
    setDuplicateModalOpen(false);
    setTicketToDuplicate(null);
    setShowServiceForm(true);
    setIsAddingNewService(true);
    setClientId(booking?.clientId ?? "");
  };

  const handleAdminComplete = (ticket: TServiceTicket) => {
    openCompleteTicket(ticket);
  };

  const handleStartService = async (ticketId: string) => {
    try {
      await mutationUpdate.mutateAsync({
        id: ticketId,
        status: JobStatus.IN_PROGRESS,
      });

      success({
        message: "Service ticket started successfully",
      });

      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKING(booking?.id || ""),
      });
    } catch (err: any) {
      error({
        message: "Failed to start service ticket",
        description: err.message,
      });
    }
  };

  const handleCancelService = async (ticketId: string) => {
    try {
      await mutationUpdate.mutateAsync({
        id: ticketId,
        status: JobStatus.SCHEDULED,
        timeIn: undefined,
        timeOut: undefined,
      });

      success({
        message: "Service cancelled, ticket moved back to scheduled",
      });

      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKING(booking?.id || ""),
      });
    } catch (err: any) {
      error({
        message: "Failed to cancel service ticket",
        description: err.message,
      });
    }
  };

  const getCompletionStatusIndicators = (ticket: any) => {
    const indicators = [];

    if (ticket.timeIn && ticket.timeOut) {
      indicators.push(
        <div
          key="time"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <Clock size={12} />
          <span>Time Logged</span>
        </div>
      );
    }

    if (ticket.clientSignature) {
      indicators.push(
        <div
          key="signature"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <FileSignature size={12} />
          <span>Signed</span>
        </div>
      );
    }

    if (ticket.staffCheckoff) {
      indicators.push(
        <div
          key="checkoff"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <User size={12} />
          <span>Staff Confirmed</span>
        </div>
      );
    }

    return indicators;
  };

  const columnsInventory = [
  {
    title: "ID",
    dataIndex: "inventoryRef",
    key: "inventoryRef",
    width: "12%",
  },
  {
    title: "Client Int Ref #",
    dataIndex: "clientInternalRef", 
    key: "clientInternalRef",
    width: "12%",
    render: (clientInternalRef: string) => clientInternalRef || "N/A",
  },
  {
    title: "Artist",
    dataIndex: "artistName",
    key: "artistName",
    width: "15%",
    render: (artistName: string) => artistName || "N/A",
  },
  {
    title: "Item",
    dataIndex: "title",
    key: "title",
    width: "20%",
  },
  {
    title: "Dimensions",
    dataIndex: "dimensions",
    key: "dimensions",
    width: "12%",
    render: (_: unknown, record: TInventory) => formatDimensions(record),
  },
  {
    title: "Packing Type",
    key: "packingType",
    width: "12%",
    render: (_: unknown, record: TInventory) => (
      <span className="text-sm">
        {formatPackingType(record)}
      </span>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: "12%",
    render: (_: unknown, record: { status: string }) => (
      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
        {capitalize(record.status)}
      </span>
    ),
  },
];

  const columnsSupply = [
    {
      title: "Item",
      key: "item",
      render: (_: unknown, record: any) => {
        if (record.isKit && record.kit) {
          return `${record.kit.name} (Kit)`;
        }
        return record.supply?.name || '-';
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Unit",
      key: "unit",
      render: (_: unknown, record: any) => {
        if (record.isKit) {
          return 'kit';
        }
        return record.supply?.unit || '-';
      },
    },
    {
      title: "Total Used",
      dataIndex: "totalUsed",
      key: "totalUsed",
      render: () => "",
    },
  ];

  if (!booking) return null;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Services</h3>
        <div className="flex items-center gap-3">
          <Segmented
            value={viewType}
            onChange={(value) => setViewType(value as "list" | "calendar")}
            options={[
              {
                label: (
                  <div className="flex items-center gap-2 px-2">
                    <List size={16} />
                    <span>List View</span>
                  </div>
                ),
                value: "list",
              },
              {
                label: (
                  <div className="flex items-center gap-2 px-2">
                    <Calendar size={16} />
                    <span>Calendar View</span>
                  </div>
                ),
                value: "calendar",
              },
            ]}
          />
          <button
            onClick={() => setShowEstimateForm(true)}
            className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Estimate</span>
          </button>
          <button
            onClick={handleAddService}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Service</span>
          </button>
        </div>
      </div>

      {/* Service Statistics */}
      {booking.serviceTicket && booking.serviceTicket.length > 0 && (
        <ServiceStats serviceTickets={booking.serviceTicket} />
      )}

      {/* Estimate Form */}
      <EstimateServiceForm />

      {/* Service Form */}
      {showServiceForm && (
        <div className="bg-blue-50/50 rounded-lg border border-blue-200 shadow-sm mb-6">
          <div className="p-4 border-b border-blue-100 flex justify-between items-center">
            <h4 className="text-lg font-medium text-gray-800">
              {isAddingNewService
                ? "Add New Service"
                : `Edit ticket ${form.getFieldValue("serial")}`}
            </h4>
            <button
              onClick={() => setShowServiceForm(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X size={16} />
            </button>
          </div>

          <Form
            form={form}
            layout="vertical"
            size="large"
            scrollToFirstError
            requiredMark={CustomizeRequiredMark}
            initialValues={{
              serviceType: ServiceType.COLLECTION,
              serviceDate: dayjs(),
              startTime: dayjs(),
            }}
          >
            {/* Service Type Tabs */}
            <Form.Item name="serviceType" noStyle>
              <ServiceTabs />
            </Form.Item>

            {/* Service Form */}
            <Spin
              spinning={
                mutation.isPending ||
                mutationUpdate.isPending ||
                deleteServiceTicket.isPending
              }
            >
              <ServiceForm handleSubmit={handleSubmit} clearForm={clearForm} />
            </Spin>
          </Form>
        </div>
      )}

      {/* Service Details - List or Calendar View */}
      {viewType === "calendar" ? (
        <ServicesCalendarView serviceTickets={booking.serviceTicket || []} />
      ) : (
        <div className="mt-6">
          {booking.serviceTicket && booking.serviceTicket.length > 0 ? (
          (() => {
            // Separate active and completed services
            const activeServices = booking.serviceTicket.filter(service =>
              service.status?.toLowerCase() !== 'completed'
            );
            const completedServices = booking.serviceTicket.filter(service =>
              service.status?.toLowerCase() === 'completed'
            );

            // Sort by date first, then by start time for same dates (null dates go to end)
            const sortServices = (a: TServiceTicket, b: TServiceTicket) => {
              // Handle null serviceDatetime - push to end
              if (!a.serviceDatetime && !b.serviceDatetime) return 0;
              if (!a.serviceDatetime) return 1;
              if (!b.serviceDatetime) return -1;

              const dateA = dayjs(a.serviceDatetime).startOf('day');
              const dateB = dayjs(b.serviceDatetime).startOf('day');

              // Compare dates first
              if (!dateA.isSame(dateB)) {
                return dateA.isBefore(dateB) ? -1 : 1;
              }

              // Same date - sort by start time (null times go to end)
              if (!a.startDatetime && !b.startDatetime) return 0;
              if (!a.startDatetime) return 1;
              if (!b.startDatetime) return -1;

              const timeA = dayjs(a.startDatetime);
              const timeB = dayjs(b.startDatetime);
              return timeA.isBefore(timeB) ? -1 : 1;
            };

            const sortedActiveServices = [...activeServices].sort(sortServices);
            const sortedCompletedServices = [...completedServices].sort(sortServices);

            // Find the selected service
            const selectedService = booking.serviceTicket.find(s => s.id === selectedServiceId);

            return (
              <div className="flex gap-4">
                {/* Left Panel - Service List */}
                <div className="w-2/5 space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                  {/* Active Services */}
                  {sortedActiveServices.map((service) => (
                    <div
                      key={service.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        selectedServiceId === service.id
                          ? "border-blue-500 bg-blue-50 shadow-md"
                          : "hover:bg-gray-50 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedServiceId(service.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          {service.serviceType === ServiceType.COLLECTION && (
                            <Truck size={16} className="text-blue-600" />
                          )}
                          {service.serviceType === ServiceType.DELIVERY && (
                            <Truck size={16} className="text-blue-600" />
                          )}
                          {service.serviceType === ServiceType.ON_SITE_SERVICE && (
                            <Box size={16} className="text-blue-600" />
                          )}
                          {service.serviceType === ServiceType.FABRICATION_SERVICE && (
                            <Box size={16} className="text-red-600" />
                          )}
                          {service.serviceType === ServiceType.WAREHOUSE_SERVICE && (
                            <Box size={16} className="text-red-600" />
                          )}
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {service.isExpoDay ? (
                                <>
                                  <span className="text-purple-700">{service.dayLabel || 'Expo Day'}</span>
                                  <Tag color="purple" className="text-xs px-1 py-0 leading-tight">EXPO</Tag>
                                </>
                              ) : (
                                capitalize(service.serviceType)
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {service.serial}
                            </div>
                          </div>
                        </div>
                        {getStatusBadge(service.status)}
                      </div>
                      <div className="mt-2 text-xs text-gray-600">
                        {service.tempLocationData?.name || service.client?.companyName || booking?.tempClientData?.name || "Quick Client"}
                      </div>
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <Calendar size={12} className="mr-1" />
                        {service.serviceDatetime
                          ? dayjs(service.serviceDatetime).format("MMM D, YYYY")
                          : "Date TBD"}
                        {service.startDatetime && (
                          <span className="ml-2">
                            <Clock size={12} className="inline mr-1" />
                            {dayjs(service.startDatetime).format("h:mm A")}
                          </span>
                        )}
                      </div>
                      {/* Expo positions count */}
                      {service.isExpoDay && service.positions && service.positions.length > 0 && (
                        <div className="mt-1 text-xs text-purple-600">
                          {service.positions.length} position{service.positions.length > 1 ? 's' : ''} •{' '}
                          {service.positions.reduce((sum, p) => sum + p.quantity, 0)} staff
                        </div>
                      )}
                      {/* Item count indicator */}
                      {service.inventories && service.inventories.length > 0 && (
                        <div className="mt-1 text-xs text-gray-500">
                          {service.inventories.length} item{service.inventories.length > 1 ? 's' : ''}
                        </div>
                      )}
                      {/* Network connect indicator */}
                      {(service.shuttleId || service.networkRouteId || service.vendorId) && (
                        <div className="mt-1.5">
                          <span className="text-xs bg-black text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-medium">
                            <Link2 size={10} />
                            {service.shuttleId
                              ? `Shuttle${service.shuttle?.shuttleRef ? ` · ${service.shuttle.shuttleRef}` : ''}`
                              : service.networkRouteId
                                ? `Network Route${service.networkRoute ? ` · ${service.networkRoute.origin} → ${service.networkRoute.destination}` : ''}`
                                : `Vendor${service.vendor?.name ? ` · ${service.vendor.name}` : ''}`}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Completed Services Section */}
                  {sortedCompletedServices.length > 0 && (
                    <>
                      <div className="border-t pt-3 mt-3">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
                          <CheckCircle size={14} className="mr-1 text-green-600" />
                          Completed ({sortedCompletedServices.length})
                        </h4>
                      </div>

                      {sortedCompletedServices.map((service) => (
                        <div
                          key={service.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            selectedServiceId === service.id
                              ? "border-blue-500 bg-blue-50 shadow-md"
                              : "hover:bg-gray-50 hover:border-gray-300 opacity-75"
                          }`}
                          onClick={() => setSelectedServiceId(service.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2">
                              {service.serviceType === ServiceType.COLLECTION && (
                                <Truck size={16} className="text-blue-600" />
                              )}
                              {service.serviceType === ServiceType.DELIVERY && (
                                <Truck size={16} className="text-blue-600" />
                              )}
                              {service.serviceType === ServiceType.ON_SITE_SERVICE && (
                                <Box size={16} className="text-blue-600" />
                              )}
                              {service.serviceType === ServiceType.FABRICATION_SERVICE && (
                                <Box size={16} className="text-red-600" />
                              )}
                              {service.serviceType === ServiceType.WAREHOUSE_SERVICE && (
                                <Box size={16} className="text-red-600" />
                              )}
                              <div>
                                <div className="font-medium text-sm flex items-center gap-2">
                                  {service.isExpoDay ? (
                                    <>
                                      <span className="text-purple-700">{service.dayLabel || 'Expo Day'}</span>
                                      <Tag color="purple" className="text-xs px-1 py-0 leading-tight">EXPO</Tag>
                                    </>
                                  ) : (
                                    capitalize(service.serviceType)
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {service.serial}
                                </div>
                              </div>
                            </div>
                            {getStatusBadge(service.status)}
                          </div>
                          <div className="mt-2 text-xs text-gray-600">
                            {service.tempLocationData?.name || service.client?.companyName || booking?.tempClientData?.name || "Quick Client"}
                          </div>
                          <div className="mt-1 flex items-center text-xs text-gray-500">
                            <Calendar size={12} className="mr-1" />
                            {service.serviceDatetime
                              ? dayjs(service.serviceDatetime).format("MMM D, YYYY")
                              : "Date TBD"}
                          </div>
                          {(service.shuttleId || service.networkRouteId || service.vendorId) && (
                            <div className="mt-1.5">
                              <span className="text-xs bg-black text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-medium">
                                <Link2 size={10} />
                                {service.shuttleId ? "Shuttle" : service.networkRouteId ? "Network Route" : "Vendor"}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* Right Panel - Service Details */}
                <div className="w-3/5 border rounded-lg bg-white">
                  {selectedService ? (
                    <div className="h-full max-h-[calc(100vh-300px)] overflow-y-auto">
                      {/* Detail Header */}
                      <div className="p-4 border-b bg-gray-50 sticky top-0 z-10">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-3">
                            {selectedService.serviceType === ServiceType.COLLECTION && (
                              <Truck size={24} className="text-blue-600" />
                            )}
                            {selectedService.serviceType === ServiceType.DELIVERY && (
                              <Truck size={24} className="text-blue-600" />
                            )}
                            {selectedService.serviceType === ServiceType.ON_SITE_SERVICE && (
                              <Box size={24} className="text-blue-600" />
                            )}
                            {selectedService.serviceType === ServiceType.FABRICATION_SERVICE && (
                              <Box size={24} className="text-red-600" />
                            )}
                            {selectedService.serviceType === ServiceType.WAREHOUSE_SERVICE && (
                              <Box size={24} className="text-red-600" />
                            )}
                            <div>
                              <div className="font-semibold text-lg">
                                {selectedService.isExpoDay ? (
                                  <span className="text-purple-700">{selectedService.dayLabel || 'Expo Day'}</span>
                                ) : (
                                  capitalize(selectedService.serviceType)
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {selectedService.serial}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {selectedService.isExpoDay && (
                              <Tag color="purple" className="flex items-center gap-1">
                                <CalendarDays size={12} />
                                EXPO
                              </Tag>
                            )}
                            {getStatusBadge(selectedService.status)}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-600">
                          <Calendar size={14} className="mr-1" />
                          {selectedService.serviceDatetime ? (
                            <>
                              {new Date(selectedService.serviceDatetime).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'numeric',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              {selectedService.startDatetime && (
                                <span className="ml-3">
                                  <Clock size={14} className="inline mr-1" />
                                  {dayjs(selectedService.startDatetime).format("h:mm A")} • Duration: {selectedService.duration || 'TBD'}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 italic">Date TBD</span>
                          )}
                        </div>
                        {/* Completion Status Indicators */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {getCompletionStatusIndicators(selectedService)}
                        </div>
                        {/* Network Connect Info */}
                        {(selectedService.shuttleId || selectedService.networkRouteId || selectedService.vendorId) && (
                          <div className="mt-2 px-3 py-2 bg-gray-900 rounded-lg flex items-center gap-2 text-sm">
                            <Link2 size={14} className="text-white" />
                            <span className="text-white font-medium">
                              {selectedService.shuttleId && (
                                <>Shuttle{selectedService.shuttle?.shuttleRef ? ` · ${selectedService.shuttle.shuttleRef}` : ''}{selectedService.shuttle?.destination ? ` → ${selectedService.shuttle.destination}` : ''}</>
                              )}
                              {selectedService.networkRouteId && (
                                <>Network Route{selectedService.networkRoute ? ` · ${selectedService.networkRoute.origin} → ${selectedService.networkRoute.destination}` : ''}</>
                              )}
                              {selectedService.vendorId && (
                                <>Vendor{selectedService.vendor?.name ? ` · ${selectedService.vendor.name}` : ''}</>
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Detail Content */}
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
                          {/* Left Column */}
                          <div className="space-y-4">
                            {/* Location */}
                            <div>
                              <div className="text-sm text-gray-500 mb-1">Location</div>
                              <div className="font-medium">
                                {selectedService.location
                                  ? formatFullAddress(selectedService.location)
                                  : selectedService.tempLocationData
                                    ? `${selectedService.tempLocationData.city || ''}${selectedService.tempLocationData.state ? ', ' + selectedService.tempLocationData.state : ''}${selectedService.tempLocationData.zip ? ' ' + selectedService.tempLocationData.zip : ''}`.trim() || 'Not specified'
                                    : 'Not specified'}
                              </div>
                            </div>

                            {/* Scope - directly below location */}
                            <div>
                              <div className="text-sm text-gray-500 mb-1">Scope</div>
                              <div className="font-medium">
                                {selectedService.scope || "Not specified"}
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="space-y-4">
                            {/* Service Client Contact + Phone - same row */}
                            <div>
                              <div className="text-sm text-gray-500 mb-1">Service Client Contact</div>
                              <div className="font-medium">
                                {selectedService.tempLocationData?.name || (selectedService.client ? getClientName(selectedService.client) : (booking?.tempClientData?.name || "Quick Client"))}
                                {selectedService.client?.contactPhone && (
                                  <span className="text-gray-600 ml-2">• {selectedService.client.contactPhone}</span>
                                )}
                              </div>
                            </div>

                            {/* Staff + Vehicle - below contact */}
                            <div>
                              <div className="text-sm text-gray-500 mb-1">Staff & Vehicle</div>
                              <div className="space-y-2">
                                {/* Vehicle */}
                                {selectedService.vessel && (
                                  <div className="flex items-center space-x-1">
                                    <Truck size={12} />
                                    <span className="text-sm font-medium">
                                      {selectedService.vessel.name}
                                    </span>
                                  </div>
                                )}

                                {/* Staff */}
                                <div className="flex flex-wrap gap-2">
                                  {selectedService.staffs?.map((staffMember, idx) => (
                                    <div
                                      key={idx}
                                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                                    >
                                      {getClientName(staffMember.user)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <div>
                            <div className="text-sm text-gray-500 mt-2 mb-1">
                              Additional Services
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {selectedService.addOnServices?.map(
                                (additionalService, idx: number) => (
                                  <div
                                    key={idx}
                                    className="flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded-full text-xs"
                                  >
                                    <Check size={12} className="text-green-600" />
                                    <span>{additionalService.name} </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Service Completion Details */}
                        {selectedService.completedAt && (
                          <ServiceCompletionDetails serviceTicket={selectedService} />
                        )}

                        {/* Estimate Templates - for ESTIMATED status */}
                        {selectedService.status === JobStatus.ESTIMATED && selectedService.estimateTemplates && selectedService.estimateTemplates.length > 0 && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-sm font-medium mb-2">Estimated Items</div>
                            <div className="space-y-1">
                              {selectedService.estimateTemplates.map((item: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <span>{item.templateName}</span>
                                  <span className="text-gray-600">
                                    Qty: {item.quantity} {item.packingType && `• ${item.packingType}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {selectedService.estimatedItemCount && (
                              <div className="mt-2 pt-2 border-t border-yellow-200 text-sm font-medium">
                                Total Est. Items: {selectedService.estimatedItemCount}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Inventory Items */}
                        <div className="mt-4">
                          <div className="text-sm font-medium mb-2">
                            Inventory Items
                          </div>

                          {/* Inventory Summary */}
                          {selectedService.inventories && selectedService.inventories.length > 0 && (() => {
                            const inventoryStats = calculateCubicFeetInventory(selectedService.inventories);
                            const sizeBreakdown = selectedService.inventories.reduce(
                              (acc, inv) => {
                                const maxDim = Math.max(inv.length || 0, inv.width || 0, inv.height || 0);
                                if (maxDim >= 55) acc.over55++;
                                else if (maxDim > 36) acc.mid++;
                                else acc.under36++;
                                return acc;
                              },
                              { under36: 0, mid: 0, over55: 0 }
                            );
                            return (
                              <div className="mb-2 p-2 bg-gray-50 border border-gray-200 rounded space-y-2">
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Total Items</div>
                                    <div className="font-semibold text-sm">{selectedService.inventories.length}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Total Volume</div>
                                    <div className="font-semibold text-sm">{inventoryStats.totalCubicFeet} ft³</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Total Weight</div>
                                    <div className="font-semibold text-sm">{inventoryStats.totalWeight} lbs</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Largest Dim ≤36&quot;</div>
                                    <div className="font-semibold text-sm">{sizeBreakdown.under36}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Largest Dim &gt;36&quot; to &lt;55&quot;</div>
                                    <div className="font-semibold text-sm">{sizeBreakdown.mid}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 mb-0.5">Largest Dim ≥55&quot;</div>
                                    <div className="font-semibold text-sm">{sizeBreakdown.over55}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          <Table
                            dataSource={selectedService.inventories}
                            columns={columnsInventory}
                            rowKey="id"
                            pagination={false}
                            className="w-full"
                            size="small"
                          />
                          
                          {/* Export Inventory Button */}
                          {selectedService.inventories && selectedService.inventories.length > 0 && (
                            <div className="mt-4 pt-4 border-t flex justify-end gap-2">
                              <button
                                onClick={() => {
                                  setExportType("csv");
                                  setIsExportModalOpen(true);
                                }}
                                className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm flex items-center space-x-1"
                              >
                                <FileSpreadsheet size={14} />
                                <span>Export CSV</span>
                              </button>
                              <button
                                onClick={() => {
                                  setExportType("pdf");
                                  setIsExportModalOpen(true);
                                }}
                                className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm flex items-center space-x-1"
                              >
                                <FileText size={14} />
                                <span>Export PDF</span>
                              </button>
                              <PrintLabels
                                selectedInventories={(selectedService.inventories || []).map(inv => ({
                                  ...inv,
                                  client: inv.client || selectedService.client || booking?.client,
                                } as TInventory))}
                              />
                            </div>
                          )}
                        </div>

                        {/* Supplies Items */}
                        <div className="mt-4">
                          <div className="text-sm font-medium mb-2">Supply Items</div>
                          <Table
                            dataSource={selectedService.supplyItems}
                            columns={columnsSupply}
                            rowKey="id"
                            pagination={false}
                            className="w-full"
                            size="small"
                          />
                        </div>

                        {/* Notes */}
                        {selectedService.staffNotes && (
                          <div className="mt-4">
                            <div className="text-sm font-medium mb-2">Notes</div>
                            <div className="p-3 bg-gray-50 border rounded-lg">
                              {selectedService.staffNotes}
                            </div>
                          </div>
                        )}

                        {/* Expo Positions Section */}
                        {selectedService.isExpoDay && selectedService.positions && selectedService.positions.length > 0 && (
                          <div className="mt-4">
                            <div className="text-sm font-medium mb-2 flex items-center gap-2">
                              <CalendarDays size={16} className="text-purple-600" />
                              <span>Expo Positions</span>
                            </div>
                            <div className="border border-purple-200 rounded-lg overflow-hidden">
                              <div className="grid grid-cols-4 gap-2 text-xs font-medium text-gray-500 bg-purple-50 px-3 py-2">
                                <div>Position</div>
                                <div>Qty</div>
                                <div>Start</div>
                                <div>Duration</div>
                              </div>
                              {selectedService.positions.map((pos, idx) => (
                                <div key={pos.id || idx} className="grid grid-cols-4 gap-2 text-sm px-3 py-2 border-t border-purple-100">
                                  <div className="font-medium">
                                    {pos.position?.name || 'Unknown Position'}
                                  </div>
                                  <div>{pos.quantity}</div>
                                  <div>{pos.startTime}</div>
                                  <div>{pos.duration} hrs</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-4 pt-4 border-t flex flex-wrap justify-end gap-2">
                          {/* Expo Mode Button - for ON_SITE_SERVICE when project is assigned */}
                          {isExpoModeAvailable && selectedService.serviceType === ServiceType.ON_SITE_SERVICE && (
                            <button
                              onClick={() => setExpoModalServiceId(selectedService.id)}
                              className="px-3 py-1.5 border-2 border-purple-400 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm flex items-center space-x-1"
                            >
                              <CalendarDays size={14} />
                              <span>Configure Expo Days</span>
                            </button>
                          )}
                          {/* Show Edit Estimate + Convert buttons for ESTIMATED services, regular Edit otherwise */}
                          {selectedService.status === JobStatus.ESTIMATED ? (
                            <>
                              <button
                                onClick={() => handleEditEstimate(selectedService)}
                                className="px-3 py-1.5 border-2 border-yellow-400 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 text-sm flex items-center space-x-1"
                              >
                                <Edit size={14} />
                                <span>Edit Estimate</span>
                              </button>
                              <button
                                onClick={() => handleConvertToService(selectedService)}
                                className="px-3 py-1.5 border-2 border-blue-400 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm flex items-center space-x-1"
                              >
                                <Check size={14} />
                                <span>Convert to Service</span>
                              </button>
                            </>
                          ) : (
                            <Popconfirm
                              title="Are you sure to edit this ticket?"
                              description="This action will discard all changes?."
                              onConfirm={() => onUseCurrentTicket(selectedService)}
                              okText="Edit"
                              cancelText="No"
                            >
                              <button className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm flex items-center space-x-1">
                                <Edit size={14} />
                                <span>Edit</span>
                              </button>
                            </Popconfirm>
                          )}
                          <button
                            onClick={() => handleOpenDuplicateModal(selectedService)}
                            className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm flex items-center space-x-1"
                          >
                            <Copy size={14} />
                            <span>Duplicate</span>
                          </button>
                          <PrintWaybill
                            size="middle"
                            selectedServiceTicketsForPrint={[
                              {
                                ...selectedService,
                                booking,
                                clientService: selectedService.client,
                              },
                            ]}
                            className="h-auto px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm flex items-center space-x-1"
                          />
                          <Popconfirm
                            title="Are you sure to delete this service ticket?"
                            description="This action cannot be undone."
                            onConfirm={() => handleDeleteServiceTicket(selectedService.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                          >
                            <button className="px-3 py-1.5 border rounded-lg hover:bg-red-50 text-red-600 text-sm flex items-center space-x-1">
                              <X size={14} />
                              <span>Delete</span>
                            </button>
                          </Popconfirm>
                          {selectedService.status === JobStatus.SCHEDULED && (
                            <button
                              onClick={() => handleStartService(selectedService.id)}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center space-x-1"
                            >
                              <Clock size={14} />
                              <span>Start</span>
                            </button>
                          )}
                          {selectedService.status === JobStatus.IN_PROGRESS && (
                            <>
                              <button
                                onClick={() => handleCancelService(selectedService.id)}
                                className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm flex items-center space-x-1"
                              >
                                <span>Cancel</span>
                              </button>
                              <button
                                onClick={() => handleAdminComplete(selectedService)}
                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center space-x-1"
                              >
                                <CheckCircle size={14} />
                                <span>Complete</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 p-8">
                      <div className="text-center">
                        <Box size={48} className="mx-auto mb-3 opacity-50" />
                        <p>Select a service from the list to view details</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
          <div className="text-center py-8 text-gray-500">
            No services found. Click "Add Service" to create your first service.
          </div>
        )}
        </div>
      )}

      <CompleteServiceTicketModal
        onSuccess={() => {
          qc.invalidateQueries({
            queryKey: BOOKING_KEYS.GET_BOOKING(booking.id),
          });
        }}
      />

      {/* Email Confirmation Modal */}
      <ConfirmationModal />

      {/* Duplicate Service Modal */}
      <DuplicateServiceModal
        ticket={ticketToDuplicate}
        open={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setTicketToDuplicate(null);
        }}
        onDuplicate={handleDuplicateService}
      />

      {/* Export Inventory Modal */}
      {(() => {
        const selectedService = booking?.serviceTicket?.find(s => s.id === selectedServiceId);
        if (!selectedService || !selectedService.inventories) return null;

        return (
          <ExportModal
            open={isExportModalOpen}
            onClose={() => setIsExportModalOpen(false)}
            exportType={exportType}
            inventories={selectedService.inventories}
            onExport={(columns, type, action) => {
              try {
                switch (type) {
                  case "csv":
                    exportToCSV(selectedService.inventories || [], columns, action);
                    break;
                  case "pdf":
                    exportToPDF(selectedService.inventories || [], columns, action);
                    break;
                }
                setIsExportModalOpen(false);
                message.success(`Export ${action === "view" ? "opened" : "downloaded"} successfully`);
              } catch (error) {
                console.error("Export error:", error);
                message.error("Failed to export. Please try again.");
              }
            }}
          />
        );
      })()}

      {/* Expo Mode Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <CalendarDays className="text-purple-600" size={20} />
            <span>Expo Mode - Multi-Day Service Configuration</span>
          </div>
        }
        open={!!expoModalServiceId}
        onCancel={() => setExpoModalServiceId(null)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {expoModalServiceId && (
          <ServiceTicketExpoForm
            serviceTicketId={expoModalServiceId}
            onClose={() => setExpoModalServiceId(null)}
          />
        )}
      </Modal>
    </div>
  );
};


const ServiceTabs = ({
  value,
  onChange,
}: {
  value?: ServiceType;
  onChange?: (val: ServiceType) => void;
}) => {
  const items = [
    {
      label: "Collection",
      value: ServiceType.COLLECTION,
    },
    {
      label: "Delivery",
      value: ServiceType.DELIVERY,
    },
    {
      label: "Site Service",
      value: ServiceType.ON_SITE_SERVICE,
    },
    {
      label: "Warehouse",
      value: ServiceType.WAREHOUSE_SERVICE,
    },
    {
      label: "Fabrication",
      value: ServiceType.FABRICATION_SERVICE,
    },
  ];

  return (
    <div className="flex border-b-2 border-blue-300 bg-blue-100">
      {items.map((item) => {
        const Icon = serviceIcons[item.value]?.icon;
        const isSelected = value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            className={`cursor-pointer px-6 transition-all ${
              isSelected
                ? "py-4 text-lg font-bold text-blue-900 border-b-4 border-blue-600 bg-blue-200"
                : "py-3 text-sm text-blue-600/70 bg-blue-50 hover:text-blue-800 hover:bg-blue-100"
            }`}
            onClick={() => onChange?.(item.value)}
          >
            <div className={`flex items-center space-x-2 ${isSelected ? "scale-105" : ""}`}>
              {Icon && <Icon size={isSelected ? 22 : 18} />}
              <span>{item.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};